import React, { useEffect, useState } from "react";
import SavOverviewCard from "../components/sav/SavOverviewCard";
import DuplicatesCard from "../components/overview/DuplicatesCard";
import IbanCheckCard from "../components/overview/IbanCheckCard";
import MissingContactsCard from "../components/overview/MissingContactsCard";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  Euro,
  CheckCircle2,
  Users,
  Package,
  Building2,
  ArrowUpRight,
  RefreshCcw,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import AppLayout, { PageHeader } from "../components/AppLayout";
import AiInsight from "../components/AiInsight";
import ApprovalsList from "../components/inbox/ApprovalsList";
import { api } from "../lib/api";
import { useLive } from "../lib/live";

const COLORS = ["#2E4734", "#4A7055", "#B8893F", "#7A9B82", "#92400E", "#A1ADA4"];

function KPICard({ overline, value, suffix, sub, trend, testid, children }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="overline mb-3">{overline}</div>
      <div className="flex items-baseline gap-2">
        <div className="kpi-value text-4xl font-semibold text-norea-text">
          {value}
          {suffix && <span className="text-xl text-norea-textMuted ml-1">{suffix}</span>}
        </div>
      </div>
      {(sub || trend !== undefined) && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          {trend !== undefined && trend !== null && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${
                trend >= 0
                  ? "text-emerald-700 bg-emerald-50"
                  : "text-rose-700 bg-rose-50"
              }`}
            >
              {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {trend >= 0 ? "+" : ""}
              {trend}%
            </span>
          )}
          {sub && <span className="text-norea-textMuted">{sub}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-norea-border rounded-md px-3 py-2 shadow-md">
      <div className="text-xs text-norea-textMuted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-norea-text">{p.name}:</span>
          <span className="font-medium text-norea-text">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function OverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/overview");
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  // Live refresh: when a dossier completes, reload KPIs immediately
  const { subscribe } = useLive();
  useEffect(() => {
    return subscribe((event) => {
      if (event.type === "dossier:completed") load();
    });
  }, [subscribe]);

  if (loading) {
    return (
      <AppLayout>
        <PageHeader title="Vue d'ensemble" subtitle="Chargement des indicateurs…" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-white border border-norea-border rounded-lg animate-pulse" />
          ))}
        </div>
      </AppLayout>
    );
  }

  const today = data?.today || {};
  const month = data?.month || {};
  const completion = data?.completion || {};
  const cost = data?.cost || {};
  const avgMs = data?.avg_duration_ms || 0;
  const avgMin = (avgMs / 60000).toFixed(1);

  return (
    <AppLayout>
      <PageHeader
        title="Vue d'ensemble"
        subtitle="Pilotage en temps réel des dossiers"
        actions={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setRefreshing(true);
                load();
              }}
              data-testid="btn-refresh-overview"
            >
              <RefreshCcw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              className="btn-primary"
              onClick={() => navigate("/dossiers")}
              data-testid="btn-go-dossiers"
            >
              Voir tous les dossiers
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </>
        }
      />
      <AiInsight scope="overview" title="Brief du jour" className="mb-6" />
      <SavOverviewCard />
      <DuplicatesCard />
      <IbanCheckCard />
      <MissingContactsCard />
      <section className="card-flat p-5 mb-6" data-testid="home-approvals">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-medium text-norea-text">À approuver</h2>
          <a href="/mails/ia?tab=approvals" className="text-xs text-norea-primary hover:underline" data-testid="home-approvals-all">Tout voir dans Mails →</a>
        </div>
        <ApprovalsList limit={3} />
      </section>

      {/* Alerts */}
      {data?.alerts?.length > 0 && (
        <div className="mb-6 space-y-2">
          {data.alerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 border-l-4 ${
                a.level === "error"
                  ? "bg-status-erreurBg border-status-erreurText"
                  : "bg-status-incompletBg border-status-incompletText"
              }`}
              data-testid={`alert-${a.level}`}
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard
          overline="Aujourd'hui"
          value={today.total ?? 0}
          sub="dossiers traités"
          trend={today.vs_yesterday_pct}
          testid="kpi-today"
        >
          <div className="mt-3 pt-3 border-t border-norea-border flex flex-wrap gap-1.5">
            {today.split && (
              <>
                <span className="badge-status badge-complet text-[10px] py-0.5">{today.split.COMPLET} OK</span>
                <span className="badge-status badge-incomplet text-[10px] py-0.5">{today.split.INCOMPLET} ⚠</span>
                <span className="badge-status badge-verifier text-[10px] py-0.5">{today.split.A_VERIFIER} ?</span>
              </>
            )}
          </div>
        </KPICard>

        <KPICard
          overline="Mois en cours"
          value={month.total ?? 0}
          sub={`Projection : ${month.projection ?? 0} fin de mois`}
          trend={month.vs_prev_pct}
          testid="kpi-month"
        />

        <KPICard
          overline="Taux de complétude"
          value={completion.rate ?? 0}
          suffix="%"
          sub="dossiers COMPLETS ce mois"
          testid="kpi-completion"
        >
          {completion.trend_6m?.length > 0 && (
            <div className="mt-3 h-10 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={completion.trend_6m}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#4A7055" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#4A7055" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="completion_rate"
                    stroke="#2E4734"
                    strokeWidth={1.5}
                    fill="url(#g1)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </KPICard>

        <KPICard
          overline="Temps moyen"
          value={avgMin}
          suffix="min"
          sub={avgMs > 0 ? `≈ ${Math.round(avgMs / 1000)}s par dossier` : "—"}
          testid="kpi-duration"
        />
      </div>

      {/* Cost row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-flat p-5" data-testid="kpi-cost">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-3">Coût IA — mois en cours</div>
              <div className="kpi-value text-4xl font-semibold text-norea-text">
                {(cost.month_eur || 0).toFixed(3)}
                <span className="text-xl text-norea-textMuted ml-1">€</span>
              </div>
              <div className="mt-2 text-xs text-norea-textMuted">
                Projection : <span className="font-medium text-norea-text">{(cost.projection_eur || 0).toFixed(3)} €</span>
                {" · "}
                Par dossier : {(cost.per_dossier_eur || 0).toFixed(5)} €
              </div>
            </div>
            <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center">
              <Euro className="w-5 h-5 text-norea-accent" />
            </div>
          </div>
        </div>

        <div className="card-flat p-5" data-testid="kpi-completion-summary">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-3">Dossiers complets</div>
              <div className="kpi-value text-4xl font-semibold text-norea-text">
                {month.split?.COMPLET ?? 0}
                <span className="text-xl text-norea-textMuted ml-1">/ {month.total ?? 0}</span>
              </div>
              <div className="mt-2 text-xs text-norea-textMuted">prêts pour CQ avant installation</div>
            </div>
            <div className="w-10 h-10 rounded-md bg-status-completBg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-status-completText" />
            </div>
          </div>
        </div>

        <div className="card-flat p-5" data-testid="kpi-perf">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline mb-3">Performance</div>
              <div className="kpi-value text-4xl font-semibold text-norea-text">
                {avgMin}
                <span className="text-xl text-norea-textMuted ml-1">min</span>
              </div>
              <div className="mt-2 text-xs text-norea-textMuted">
                {avgMs <= 180000 ? (
                  <span className="text-emerald-700">✓ Sous le seuil de 3 min</span>
                ) : (
                  <span className="text-status-incompletText">⚠ Au-dessus de 3 min</span>
                )}
              </div>
            </div>
            <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center">
              <Clock className="w-5 h-5 text-norea-accent" />
            </div>
          </div>
        </div>
      </div>

      {/* Main chart */}
      <div className="card-flat p-6 mb-6" data-testid="chart-30d">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl font-medium text-norea-text">Évolution sur 30 jours</h3>
            <p className="text-xs text-norea-textMuted mt-1">Dossiers traités par jour, ventilés par statut</p>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.trend_30d || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E6DF" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#58695C" }} axisLine={{ stroke: "#E4E6DF" }} />
              <YAxis tick={{ fontSize: 11, fill: "#58695C" }} axisLine={{ stroke: "#E4E6DF" }} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="complet" name="Complet" stroke="#2E4734" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="incomplet" name="Incomplet" stroke="#B8893F" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="a_verifier" name="À vérifier" stroke="#9A3412" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="erreur" name="Erreur" stroke="#991B1B" strokeWidth={1.5} dot={false} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Splits */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SplitCard title="Par partenaire" icon={Users} data={data?.split_partner} testid="split-partner" />
        <SplitCard title="Par produit" icon={Package} data={data?.split_produit} testid="split-produit" />
        <SplitCard title="Par organisme" icon={Building2} data={data?.split_organisme} testid="split-organisme" />
      </div>
    </AppLayout>
  );
}

function SplitCard({ title, icon: Icon, data = [], testid }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-norea-accent" />
        <h3 className="font-heading text-base font-medium text-norea-text">{title}</h3>
      </div>
      {data.length === 0 ? (
        <div className="text-xs text-norea-textMuted py-8 text-center">Aucune donnée</div>
      ) : (
        <>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={40} outerRadius={70} paddingAngle={2}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.slice(0, 5).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-norea-text truncate">{d.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-norea-textMuted">{Math.round((d.value / total) * 100)}%</span>
                  <span className="font-medium text-norea-text tabular-nums">{d.value}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
